/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 * Generates the theme object for the component from the theme and provided additional information
 * @param  {Object} theme The actual theme object.
 * @return {Object} The final theme object with the overrides and component variables
 */
const generateComponentTheme = theme => {
  var _colors$contrasts, _colors$contrasts2, _colors$contrasts3, _colors$contrasts4, _colors$contrasts5, _colors$contrasts6, _colors$contrasts7, _colors$contrasts8, _colors$contrasts9;
  const colors = theme.colors,
    borders = theme.borders,
    typography = theme.typography,
    themeName = theme.key,
    spacing = theme.spacing;
  const themeSpecificStyles = {
    canvas: {
      iconColor: theme['ic-brand-font-color-dark'],
      hoverBackgroundColor: theme['ic-brand-primary'],
      focusOutlineColor: theme['ic-brand-primary'],
      nameTextColor: theme['ic-brand-primary'],
      descriptorTextColor: theme['ic-brand-font-color-dark']
    }
  };
  const componentVariables = {
    hoverBackgroundColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts = colors.contrasts) === null || _colors$contrasts === void 0 ? void 0 : _colors$contrasts.blue4570,
    hoverTextColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts2 = colors.contrasts) === null || _colors$contrasts2 === void 0 ? void 0 : _colors$contrasts2.white1010,
    focusOutlineWidth: borders === null || borders === void 0 ? void 0 : borders.widthMedium,
    focusOutlineColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts3 = colors.contrasts) === null || _colors$contrasts3 === void 0 ? void 0 : _colors$contrasts3.blue4570,
    focusOutlineStyle: borders === null || borders === void 0 ? void 0 : borders.style,
    iconColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts4 = colors.contrasts) === null || _colors$contrasts4 === void 0 ? void 0 : _colors$contrasts4.grey125125,
    iconsMarginRight: spacing === null || spacing === void 0 ? void 0 : spacing.xSmall,
    descriptorMarginTop: spacing === null || spacing === void 0 ? void 0 : spacing.xxxSmall,
    descriptorTextColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts5 = colors.contrasts) === null || _colors$contrasts5 === void 0 ? void 0 : _colors$contrasts5.grey125125,
    descriptorFontSizeSmall: typography === null || typography === void 0 ? void 0 : typography.fontSizeXSmall,
    descriptorFontSizeMedium: typography === null || typography === void 0 ? void 0 : typography.fontSizeXSmall,
    descriptorFontSizeLarge: typography === null || typography === void 0 ? void 0 : typography.fontSizeSmall,
    nameTextColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts6 = colors.contrasts) === null || _colors$contrasts6 === void 0 ? void 0 : _colors$contrasts6.blue4570,
    nameFontSizeSmall: typography === null || typography === void 0 ? void 0 : typography.fontSizeXSmall,
    nameFontSizeMedium: typography === null || typography === void 0 ? void 0 : typography.fontSizeSmall,
    nameFontSizeLarge: typography === null || typography === void 0 ? void 0 : typography.fontSizeMedium,
    baseSpacingSmall: spacing === null || spacing === void 0 ? void 0 : spacing.xSmall,
    baseSpacingMedium: spacing === null || spacing === void 0 ? void 0 : spacing.small,
    baseSpacingLarge: '1rem',
    borderWidth: borders === null || borders === void 0 ? void 0 : borders.widthSmall,
    borderRadius: borders === null || borders === void 0 ? void 0 : borders.radiusMedium,
    borderColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts7 = colors.contrasts) === null || _colors$contrasts7 === void 0 ? void 0 : _colors$contrasts7.grey4570,
    textLineHeight: typography === null || typography === void 0 ? void 0 : typography.lineHeightCondensed,
    selectedTextColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts8 = colors.contrasts) === null || _colors$contrasts8 === void 0 ? void 0 : _colors$contrasts8.white1010,
    selectedBackgroundColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts9 = colors.contrasts) === null || _colors$contrasts9 === void 0 ? void 0 : _colors$contrasts9.grey4570,
    selectedOutlineWidth: borders === null || borders === void 0 ? void 0 : borders.widthLarge
  };
  return {
    ...componentVariables,
    ...themeSpecificStyles[themeName]
  };
};
export default generateComponentTheme;